import React, { useState, useEffect } from "react";
import { Layout, Menu, Card, Typography } from "antd";
import { motion, AnimatePresence } from "framer-motion";
import pose from "../../Assets/variation/pose.jpg";
import { API_BASE_URL } from "../../Config";

const { Sider, Content } = Layout;
const { Title, Paragraph, Text } = Typography;

const Variation = () => {
  const [selectedKey, setSelectedKey] = useState("HOME");
  const [screenWidth, setScreenWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  const [variations, setVariations] = useState([]);
  const [selectedVariation, setSelectedVariation] = useState(null);

  const handleMenuClick = async (e) => {
    setSelectedKey(e.key);

    if (
      e.key !== "HOME" &&
      e.key !== "HAND" &&
      e.key !== "LEG" &&
      e.key !== "OTHER"
    ) {
      try {
        const res = await fetch(`${API_BASE_URL}/makevariations/${e.key}`);
        const data = await res.json();
        setSelectedVariation(data);
      } catch (error) {
        console.log(error);
      }
    } else {
      setSelectedVariation(null);
    }
  };

  useEffect(() => {
    const onResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = screenWidth < 768;

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/makevariations/names`);
        const data = await res.json();

        console.log("API response:", data);

        // FIX: backend sends { message, data: [] }
        if (Array.isArray(data.data)) {
          setVariations(data.data);
        } else {
          console.error("Backend did not return array:", data);
          setVariations([]);
        }
      } catch (error) {
        console.log(error);
        setVariations([]);
      }
    };

    load();
  }, []);

  const handItems = variations
    .filter((v) => v.variationType?.includes("Hand variations"))
    .map((v) => ({
      key: v._id,
      label: v.variationName,
    }));

  const legItems = variations
    .filter((v) => v.variationType?.includes("variations on legs"))
    .map((v) => ({
      key: v._id,
      label: v.variationName,
    }));

  const otherItems = variations
    .filter((v) => v.variationType?.includes("other variations"))
    .map((v) => ({
      key: v._id,
      label: v.variationName,
    }));

  const fadeSlide = {
    hidden: { opacity: 0, y: 18 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.36 } },
    exit: { opacity: 0, y: -12, transition: { duration: 0.24 } },
  };

  const menuItemStyle = {
    background: "rgba(255, 255, 255, 0.25)",
    borderRadius: "6px",
    fontWeight: "bold",
  };

  const renderContent = () => {
    if (selectedKey === "HOME") {
      return (
        <>
          <Title level={2} style={{ textAlign: "center", marginBottom: 6 }}>
            How to Make Variations
          </Title>
          <Paragraph
            style={{
              textAlign: "left",
              maxWidth: 900,
              margin: "10px auto",
              lineHeight: 1.6,
            }}
          >
            Combining other asanas or mudras with the asanas we are learning
            creates variations in yoga asanas. Below are just a few of the
            positions that I know of that help create variations. Please help
            everyone by sending us your knowledge about yoga variations on our
            website.
          </Paragraph>

          <Paragraph
            style={{
              textAlign: "left",
              maxWidth: 900,
              margin: "0 auto 12px",
              lineHeight: 1.6,
            }}
          >
            Those who are new to asanas should practice with the advice and
            guidance of a yoga teacher.
          </Paragraph>

          <h3 style={{ color: "red", marginTop: 12 }}>Important tips:</h3>
          <p style={{ lineHeight: 1.6 }}>
            Not all of the above can be used in one asana. Only the variations
            that are suitable for that asana can be used.
          </p>
          <p>For example:</p>
          <ol style={{ marginLeft: 20 }}>
            <li>Pada Garudasana cannot be done in Padmasana.</li>
            <li>Pada Gomukhasana cannot be done in Padmasana.</li>
          </ol>
          <p style={{ marginTop: 6 }}>But</p>
          <ol style={{ marginLeft: 20 }}>
            <li>Hasta Garudasana can be done in Padmasana.</li>
            <li>Hasta Gomukhasana can be done in Padmasana.</li>
          </ol>

          <Paragraph style={{ lineHeight: 1.6, marginTop: 8 }}>
            Because Padmasana can be done with the feet, other variations that
            can be done with the feet cannot be done in it. So variations that
            can be done with the hands can be done in it.
          </Paragraph>

          <Paragraph style={{ lineHeight: 1.6 }}>
            Try all the variations that are suitable for your asana.
          </Paragraph>

          <Title level={4} style={{ marginTop: 12 }}>
            Try and send us pictures of new variations and your pictures on our
            website.
          </Title>
        </>
      );
    }

    if (selectedVariation) {
      return (
        <>
          <div
            style={{
              width: "100%",
              borderRadius: 12,
              overflow: "hidden",
              marginBottom: 20,
              background: "#f7f7f7",
            }}
          >
            <img
              src={selectedVariation.variationImage}
              alt={selectedVariation.variationName}
              style={{
                width: "100%",
                maxWidth: "450px",
                height: "auto",
                display: "block",
                margin: "20px auto",
                borderRadius: 8,
              }}
            />
          </div>

          <h2 style={{ margin: 0, fontSize: 22 }}>
            {selectedVariation.variationName}
          </h2>

          <p style={{ margin: "12px 0 8px", fontSize: 15 }}>
            <Text strong>Sanskrit:</Text> {selectedVariation.sanskrit}
          </p>

          <p style={{ margin: "8px 0 12px", fontSize: 15 }}>
            <Text strong>English:</Text> {selectedVariation.english}
          </p>

          <p style={{ margin: "8px 0 0", fontSize: 15 }}>
            <Text strong>Example Asanas:</Text> {selectedVariation.exampleAsana}
          </p>
        </>
      );
    }

    return <Paragraph>No content found.</Paragraph>;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "stretch",
        minHeight: "100vh",
        padding: "30px 10px",
        background: "#f0f2f5",
      }}
    >
      <Layout
        style={{
          width: isMobile ? "100%" : "90%",
          maxWidth: "1340px",
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          overflow: "hidden",
          height: "auto",
        }}
      >
        {/* SIDEBAR */}
        <div
          className="manrope_font"
          style={{
            width: isMobile ? "100%" : "200px",
            height: isMobile ? "auto" : "100%",
          }}
        >
          <Sider
            className="makeVariation_Slider"
            width={isMobile ? "100%" : 240}
            style={{
              position: isMobile ? "relative" : "sticky",
              top: isMobile ? "0px" : "64px",
              marginTop: isMobile ? "60px" : "0px",
              height: isMobile ? "auto" : "calc(100vh - 64px)",

              backgroundImage: `url(${pose})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(0, 0, 0, 0.6)",
                zIndex: 1,
              }}
            />

            <div
              className="manrope_font"
              style={{
                position: "relative",
                zIndex: 2,
                padding: "16px",
                textAlign: "center",
                color: "#fff",
                fontSize: "1rem",
              }}
            >
              How to make variations
            </div>

            <Menu
              theme="white"
              mode="inline"
              selectedKeys={[selectedKey]}
              onClick={handleMenuClick}
              style={{
                background: "transparent",
                color: "#ffffffA2",
                fontSize: "1.1rem",
                position: "relative",
                zIndex: 2,
                fontWeight: 500,
                textAlign: "center",
              }}
              itemStyle={{ color: "#fff" }}
              selectedItemStyle={{
                background: "rgba(255, 255, 255, 0.45)",
                borderRadius: 6,
                paddingLeft: "0px",
                fontWeight: 600,
                color: "#000",
              }}
              items={[
                {
                  key: "HAND",
                  label: "Hand Variations",
                  children: handItems,
                },
                {
                  key: "LEG",
                  label: "Variations on Legs",
                  children: legItems,
                },
                {
                  key: "OTHER",
                  label: "Other Variations",
                  children: otherItems,
                },
                {
                  key: "HOME",
                  label: "Main menu",
                },
              ]}
            />
          </Sider>
        </div>

        {/* MAIN CONTENT */}
        <Content
          style={{
            flex: 1,
            padding: isMobile ? "10px" : "30px 40px",
            background: "#fff",
            textAlign: "left",
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedKey}
              variants={fadeSlide}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <Card
                style={{
                  borderRadius: "12px",
                  background: "#ffffff",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                  padding: "24px",
                }}
              >
                {renderContent()}
              </Card>
            </motion.div>
          </AnimatePresence>
        </Content>
      </Layout>
    </motion.div>
  );
};

export default Variation;
